import { useEffect, useState } from "react";

interface SchemaItem {
    content?: {
        alias: string;
        props: Record<string, unknown>;
    };
}

interface PageData {
    props?: {
        schemaType?: SchemaItem[];
    };
}

interface SchemaMarkup {
    "@context": string;
    "@type": string;
    [key: string]: unknown;
}

/**
 * Custom hook for generating dynamic schema markup from page data
 *
 * This hook automatically:
 * - Generates Schema.org compliant JSON-LD markup from page content
 * - Sets a global flag to communicate with footer components about schema existence
 * - Handles cleanup when the component unmounts
 *
 * @param pageData - Page data containing schema information
 * @returns Array of Schema.org compliant objects ready for JSON-LD insertion
 *
 * @example
 * ```tsx
 * import { useSchemaMarkup } from "@danishagro/shared/src/hooks/useSchemaMarkup.hook";
 *
 * const MyPage = ({ pageContent }) => {
 *   const dynamicSchemas = useSchemaMarkup(pageContent);
 *
 *   return (
 *     <>
 *       <Helmet>
 *         {dynamicSchemas.map((schema, index) => (
 *           <script key={index} type="application/ld+json">
 *             {JSON.stringify(schema)}
 *           </script>
 *         ))}
 *       </Helmet>
 *       // ... rest of component
 *     </>
 *   );
 * };
 * ```
 */
export const useSchemaMarkup = (pageData?: PageData): SchemaMarkup[] => {
    const [dynamicSchemas, setDynamicSchemas] = useState<SchemaMarkup[]>([]);

    // Generate dynamic schema markup from pageData
    const generateSchemaMarkup = (data?: PageData) => {
        const storedSchemaType = data?.props?.schemaType;

        if (!storedSchemaType || !Array.isArray(storedSchemaType)) {
            return [];
        }

        // Schema.org property mapping for better field names
        const schemaPropertyMap: Record<string, string> = {
            vatID: "vatID",
            street: "streetAddress",
            locality: "addressLocality",
            region: "addressRegion",
            postalCode: "postalCode",
            country: "addressCountry",
            telephone: "telephone",
            email: "email",
            openingHours: "openingHours",
            description: "description",
            logo: "logo",
            image: "image",
            name: "name",
            url: "url",
        };

        // Helper function to convert any props object to schema format
        const convertPropsToSchema = (
            props: Record<string, unknown>,
            excludeFields: string[] = []
        ): Record<string, unknown> => {
            const schema: Record<string, unknown> = {};

            Object.keys(props).forEach((key) => {
                if (excludeFields.includes(key)) return;

                const value = props[key];
                if (!value) return;

                // Handle nested address arrays (special case)
                if (key === "address" && Array.isArray(value)) {
                    const firstAddress = value[0];
                    if (firstAddress?.content?.props) {
                        schema.address = {
                            "@type": "PostalAddress",
                            ...convertPropsToSchema(firstAddress.content.props),
                        };
                    }
                    return;
                }

                // Skip complex objects and arrays (except address handled above)
                if (typeof value === "object" && !Array.isArray(value)) return;
                if (Array.isArray(value) && key !== "address") return;

                // Map to proper Schema.org property name
                const schemaKey = schemaPropertyMap[key] || key;
                schema[schemaKey] = value;
            });

            return schema;
        };

        return storedSchemaType
            .map((schemaItem: SchemaItem) => {
                const alias = schemaItem.content?.alias;
                const props = schemaItem.content?.props;

                if (!alias || !props) return null;

                // Convert alias to proper Schema.org @type
                const schemaTypeMap: Record<string, string> = {
                    organization: "Organization",
                    postalAddress: "PostalAddress",
                    webSite: "WebSite",
                    localBusiness: "LocalBusiness",
                    person: "Person",
                    product: "Product",
                    service: "Service",
                    event: "Event",
                    article: "Article",
                    blogPosting: "BlogPosting",
                    newsArticle: "NewsArticle",
                };

                const schemaType =
                    schemaTypeMap[alias] || alias.charAt(0).toUpperCase() + alias.slice(1);

                // Base schema with context and type
                const baseSchema: SchemaMarkup = {
                    "@context": "https://schema.org",
                    "@type": schemaType,
                };

                // Add page name as fallback for name field if not present
                if (!props.name && (alias === "organization" || alias === "localBusiness")) {
                    const siteSettings = (
                        globalThis as unknown as {
                            siteSettingsObj?: { footer?: { columns?: Array<{ header?: string }> } };
                        }
                    )?.siteSettingsObj;
                    const siteName = siteSettings?.footer?.columns?.[0]?.header || "Danish Agro";
                    baseSchema.name = siteName;
                }

                // Add current URL for website schema if not present
                if (!props.url && alias === "webSite") {
                    baseSchema.url = typeof window !== "undefined" ? window.location.origin : "";
                }

                // Convert all props dynamically
                const dynamicProps = convertPropsToSchema(props);

                return {
                    ...baseSchema,
                    ...dynamicProps,
                };
            })
            .filter(Boolean);
    };

    // Generate schemas when pageData changes
    useEffect(() => {
        const schemas = generateSchemaMarkup(pageData);
        setDynamicSchemas(schemas);
    }, [pageData]);

    // Set global flag to indicate whether dynamic schemas exist
    useEffect(() => {
        (globalThis as unknown as { hasDynamicSchemas?: boolean }).hasDynamicSchemas =
            dynamicSchemas.length > 0;

        return () => {
            // Cleanup when component unmounts
            (globalThis as unknown as { hasDynamicSchemas?: boolean }).hasDynamicSchemas = false;
        };
    }, [dynamicSchemas.length]);

    return dynamicSchemas;
};
